import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    let isActive = true;
    let activeSocket = null;

    if (isAuthenticated && user?.id) {
      const socketUrl = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace('/api', '')
        : 'http://localhost:5001';

      import('socket.io-client').then(({ io }) => {
        if (!isActive) {
          return;
        }

        activeSocket = io(socketUrl, {
          auth: {
            token: localStorage.getItem('token')
          },
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 2000,
          timeout: 20000,
          forceNew: true
        });

        activeSocket.on('connect', () => {
          console.log('Connected to server');
          setConnectionStatus('connected');
          activeSocket.emit('join', {
            userType: user.userType,
            userId: user.id
          });
          activeSocket.emit('join-room', user.id);
        });

        activeSocket.on('disconnect', () => {
          console.log('Disconnected from server');
          setConnectionStatus('disconnected');
        });

        activeSocket.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          setConnectionStatus('error');
        });

        activeSocket.on('order-alert', (data) => {
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'order',
            title: 'New Order Received',
            message: `Order #${data.orderNumber} from ${data.customerName}`,
            timestamp: new Date(),
            data
          }]);
        });

        activeSocket.on('order-status-update', (data) => {
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'status',
            title: 'Order Status Updated',
            message: `Order #${data.orderNumber} is now ${data.status}`,
            timestamp: new Date(),
            data
          }]);
        });

        activeSocket.on('rider-location', (data) => {
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'location',
            title: 'Rider Location Update',
            message: 'Your delivery rider is on the way',
            timestamp: new Date(),
            data
          }]);
        });

        activeSocket.on('new-order', (data) => {
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'new-order',
            title: 'New Order Alert',
            message: `New order #${data.orderNumber} from ${data.customerName}`,
            timestamp: new Date(),
            data
          }]);
        });

        activeSocket.on('notification', (data) => {
          setNotifications(prev => [...prev, {
            id: data._id || Date.now(),
            type: data.type,
            title: data.title,
            message: data.message,
            timestamp: new Date(data.createdAt),
            data: data.data,
            isRead: data.isRead
          }]);
        });

        activeSocket.on('rider-assigned', (data) => {
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'rider-assigned',
            title: 'Rider Assigned',
            message: 'Your order has been assigned to a rider',
            timestamp: new Date(),
            data
          }]);
        });

        setSocket(activeSocket);
      });
    } else {
      setConnectionStatus('disconnected');
      setSocket(null);
    }

    return () => {
      isActive = false;
      if (activeSocket) {
        activeSocket.close();
      }
      setSocket(null);
    };
  }, [isAuthenticated, user?.id, user?.userType]);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [socket]);

  const emitEvent = (event, data) => {
    if (socket) {
      socket.emit(event, data);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const value = {
    socket,
    notifications,
    connectionStatus,
    emitEvent,
    removeNotification,
    clearNotifications
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
